import normalizeOptionSku from './normalizeOptionSku';

/**
 * Restricts each enriched product's variants to a per-product SKU allowlist
 * (the hit's qualifying SKUs from `getQualifyingHitVariants`), and drops
 * products with no match at all. Both sides of the join go through
 * `normalizeOptionSku` — the allowlist is built normalized upstream, and the
 * enriched variant SKU is normalized here.
 *
 * Both sides are keyed through `normalizeOptionSku`, since the enriched
 * variants carry full SKUs and the allowlist comes from Caravel's option SKUs.
 *
 * `variantsTotal` is rewritten to the matched count so the swatch limiter's
 * "+N more" arithmetic counts matched upholstery, not the full catalogue.
 *
 * Enrichment is upholstery-only (defaults for every other option), so an
 * allowlisted SKU that is a non-default option combination has no enriched
 * counterpart and silently disappears — the accepted under-rendering of
 * CON-8561 decision (b). Matched variants keep their enriched order.
 *
 * `keepUnmatchedProducts` (CON-8567 zero-drop fallback) keeps a zero-match
 * product instead of dropping it, with its full variant list untouched and
 * `isVariantMatchingFallback: true` so the card builder badges it like a plain
 * category card. Membership is never in doubt — the product only appears
 * because Algolia matched its warehouse clause — so the card is correct, just
 * warehouse-blind in which swatches it shows. Descriptor-gated: only valid
 * where membership is product-true (ready-to-ship), not SKU-specific
 * (clearance / flash sale).
 */
const filterProductsToMatchedVariants = ({
  products,
  matchedSkusByProductId,
  keepUnmatchedProducts = false,
}) => {
  if (!matchedSkusByProductId) {
    return products;
  }

  return (products || [])
    .map(product => {
      const allowedSkus = matchedSkusByProductId.get(String(product.id));
      const matchedVariants = allowedSkus
        ? (product.variants || []).filter(variant =>
            allowedSkus.has(normalizeOptionSku(variant?.sku))
          )
        : [];

      if (!matchedVariants.length) {
        return keepUnmatchedProducts
          ? { ...product, isVariantMatchingFallback: true }
          : null;
      }

      return {
        ...product,
        variants: matchedVariants,
        variantsTotal: matchedVariants.length,
      };
    })
    .filter(Boolean);
};

export default filterProductsToMatchedVariants;
